/**
 * Rules of the "edit a registration form" screens (admin and personal link). They mirror
 * _apply_registration_edit in 20261029000001_registration_edit_links.sql, which enforces them
 * on the server; here they only decide what to show and when to enable "Guardar".
 */

import type {
  EditableFormKey,
  RegistrationEditPayload,
  RegistrationEditView,
  RegistrationFormType,
  RegistrationKind,
} from './types';

export type EditFormValues = Record<EditableFormKey, string>;

const EMPTY: EditFormValues = {
  full_name: '',
  cell_phone: '',
  email: '',
  office_phone: '',
  address: '',
  birth_date: '',
  position: '',
  district_id: '',
  region_id: '',
  represented_minister_id: '',
  represented_name: '',
  represented_ci: '',
  institution_note: '',
};

/** Same fields the public registration form lets this kind of person edit */
export function editableFields(kind: RegistrationKind, formType: RegistrationFormType): Set<EditableFormKey> {
  const full = formType === 'FULL_CONVENTION';
  const keys: EditableFormKey[] = ['full_name', 'cell_phone', 'email'];
  if (full) keys.push('office_phone', 'address');
  if (kind === 'MINISTER') {
    keys.push('birth_date');
  } else {
    if (full) keys.push('position');
    if (kind === 'GUEST') keys.push('district_id', 'region_id');
    if (kind === 'DELEGATE') keys.push('represented_minister_id', 'represented_name', 'represented_ci', 'institution_note');
  }
  return new Set(keys);
}

export function initialEditValues(view: RegistrationEditView): EditFormValues {
  const f = view.form as Record<string, unknown>;
  const text = (key: EditableFormKey) => (typeof f[key] === 'string' ? (f[key] as string) : '');
  const values = { ...EMPTY };
  (Object.keys(EMPTY) as EditableFormKey[]).forEach((key) => {
    values[key] = text(key);
  });
  return values;
}

/** Only what changed, and only fields this person may edit */
export function buildEditPayload(
  initial: EditFormValues,
  current: EditFormValues,
  editable: Set<EditableFormKey>,
  documents: RegistrationEditPayload['documents'],
): RegistrationEditPayload {
  const form: RegistrationEditPayload['form'] = {};
  (Object.keys(current) as EditableFormKey[]).forEach((key) => {
    if (!editable.has(key)) return;
    if (current[key].trim() === initial[key].trim()) return;
    form[key] = key === 'represented_minister_id' && current[key] === '' ? null : current[key].trim();
  });
  return { form, documents };
}

export function hasChanges(payload: RegistrationEditPayload): boolean {
  return Object.keys(payload.form).length > 0 || Object.keys(payload.documents).length > 0;
}

/** What is still missing before "Guardar" is enabled */
export function missingForSave(
  kind: RegistrationKind,
  current: EditFormValues,
  editable: Set<EditableFormKey>,
  signature: { required: boolean; present: boolean },
): string[] {
  const missing: string[] = [];
  if (editable.has('full_name') && current.full_name.trim().length < 3) missing.push('Nombre completo');
  if (editable.has('cell_phone') && !current.cell_phone.trim()) missing.push('Celular');
  if (kind === 'DELEGATE' && !current.represented_minister_id && !current.represented_name.trim()) {
    missing.push('A quién representa');
  }
  if (signature.required && !signature.present) missing.push('Firma');
  return missing;
}

/** Digital-signature registration still without its signature (not rejected) */
export function needsSignature(p: {
  registration_status: string;
  form_signed_at: string | null;
}, signatureMode: string | null | undefined): boolean {
  return signatureMode === 'DIGITAL' && !p.form_signed_at && p.registration_status !== 'REJECTED';
}
